import { HelpCircle } from 'lucide-react';

export function InfoPanel() {
  return (
    <div className="space-y-6">
      <div className="bg-blue-50 rounded-lg p-6">
        <h2 className="flex items-center text-lg font-medium text-blue-900 mb-2">
          <HelpCircle className="h-5 w-5 mr-2" />
          Understanding Your CTC
        </h2>
        <div className="space-y-4 text-sm text-blue-800">
          <p>
            Cost to Company (CTC) is the total amount your employer spends on you annually,
            including all benefits and contributions.
          </p>
          <p>
            Your take-home salary will be lower than your CTC due to various deductions and
            the fact that some components are non-monetary benefits.
          </p>
        </div>
      </div>

      <div className="space-y-4">
        <details className="group">
          <summary className="flex items-center justify-between cursor-pointer list-none">
            <span className="text-gray-900 font-medium">What is Basic Salary?</span>
            <span className="transition group-open:rotate-180">
              <svg
                fill="none"
                height="24"
                shape-rendering="geometricPrecision"
                stroke="currentColor"
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="1.5"
                viewBox="0 0 24 24"
                width="24"
              >
                <path d="M6 9l6 6 6-6" />
              </svg>
            </span>
          </summary>
          <p className="text-gray-600 mt-2">
            Basic salary is the fixed amount paid before any additional allowances or
            deductions. It typically forms 40-50% of your CTC and is the base for calculating
            various other components.
          </p>
        </details>

        <details className="group">
          <summary className="flex items-center justify-between cursor-pointer list-none">
            <span className="text-gray-900 font-medium">Understanding HRA</span>
            <span className="transition group-open:rotate-180">
              <svg
                fill="none"
                height="24"
                shape-rendering="geometricPrecision"
                stroke="currentColor"
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="1.5"
                viewBox="0 0 24 24"
                width="24"
              >
                <path d="M6 9l6 6 6-6" />
              </svg>
            </span>
          </summary>
          <p className="text-gray-600 mt-2">
            House Rent Allowance (HRA) is a component of salary that an employer provides to
            employees to meet their rental expenses. It's typically 40-50% of basic salary
            for metro cities.
          </p>
        </details>

        <details className="group">
          <summary className="flex items-center justify-between cursor-pointer list-none">
            <span className="text-gray-900 font-medium">Tax Benefits</span>
            <span className="transition group-open:rotate-180">
              <svg
                fill="none"
                height="24"
                shape-rendering="geometricPrecision"
                stroke="currentColor"
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="1.5"
                viewBox="0 0 24 24"
                width="24"
              >
                <path d="M6 9l6 6 6-6" />
              </svg>
            </span>
          </summary>
          <p className="text-gray-600 mt-2">
            Components like HRA, LTA, and EPF contributions can help reduce your taxable
            income. Consult a tax professional to understand how to maximize your tax
            benefits.
          </p>
        </details>

        <details className="group">
          <summary className="flex items-center justify-between cursor-pointer list-none">
            <span className="text-gray-900 font-medium">Understanding EPF</span>
            <span className="transition group-open:rotate-180">
              <svg
                fill="none"
                height="24"
                shape-rendering="geometricPrecision"
                stroke="currentColor"
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="1.5"
                viewBox="0 0 24 24"
                width="24"
              >
                <path d="M6 9l6 6 6-6" />
              </svg>
            </span>
          </summary>
          <p className="text-gray-600 mt-2">
            EPF is commonly calculated on Basic + DA. Some employers cap the monthly PF
            wage at Rs 15,000, making 12% EPF Rs 1,800 per month, while others contribute
            on actual wages or a custom wage base.
          </p>
        </details>

        <details className="group">
          <summary className="flex items-center justify-between cursor-pointer list-none">
            <span className="text-gray-900 font-medium">Standard Deduction</span>
            <span className="transition group-open:rotate-180">
              <svg
                fill="none"
                height="24"
                shape-rendering="geometricPrecision"
                stroke="currentColor"
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="1.5"
                viewBox="0 0 24 24"
                width="24"
              >
                <path d="M6 9l6 6 6-6" />
              </svg>
            </span>
          </summary>
          <div className="text-gray-600 mt-2 space-y-2">
            <p>
              Standard deduction is a flat amount that reduces your taxable income:
            </p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Old Tax Regime: ₹50,000 per year</li>
              <li>New Tax Regime (Tax Year 2026-27): ₹75,000 per year</li>
            </ul>
          </div>
        </details>
      </div>

      <div className="space-y-4">
        <details className="group">
          <summary className="cursor-pointer text-gray-900 font-medium">How do NPS contributions affect my take-home salary?</summary>
          <div className="text-gray-600 mt-2 space-y-2">
            <p>In Advanced Settings, enter employee and employer Tier I NPS contributions separately as monthly amounts.
              Employee payroll contributions reduce cash take-home. Employer NPS is allocated from Special Allowance
              within CTC and deposited into your pension account; it is not bank salary. Employer matching is not automatic.</p>
            <p>For Tax Year 2026–27, Section 124(1)–(2) (formerly 80CCD(2)) allows employer NPS deductions up to
              14% of Basic + eligible DA in the new regime. In the old regime the limit is 10% for other employers
              and 14% for Central / State Government employers. Other allowances are excluded from this salary base.</p>
            <p>The additional personal NPS deduction under Section 124(3) (formerly 80CCD(1B)) is up to ₹50,000/year
              in the old regime. The calculator combines payroll NPS with the annual personal NPS field for this cap;
              do not enter the same contribution twice. Eligible contributions under the shared ₹1.5 lakh limit
              can be included in the existing Section 80C field. Personal NPS deductions are unavailable in the new regime.</p>
            <p>Combined employer contributions to recognised PF, NPS and approved superannuation above ₹7.5 lakh/year,
              and related accretion, can create additional taxable perquisites that this estimate does not model.</p>
            <a className="text-indigo-600 underline" href="https://www.incometaxindia.gov.in/w/deductions" target="_blank" rel="noopener noreferrer">Income Tax Department: NPS deduction guidance</a>
          </div>
        </details>
        <details className="group">
          <summary className="cursor-pointer text-gray-900 font-medium">Are meal coupons or meal cards tax-free?</summary>
          <div className="text-gray-600 mt-2 space-y-2">
            <p>From 1 April 2026, Rule 15(5)(a), Table IV of the Income-tax Rules, 2026 allows eligible employer-provided
              meal vouchers usable only at eating joints during working hours to be exempt up to ₹200 per meal
              in either tax regime. A cash food allowance is taxable salary.</p>
            <p>Select meal coupons in Advanced Settings and confirm eligibility only if your employer's arrangement qualifies.
              Enter the actual working days and eligible meals per day. For example, 22 days × 1 meal × ₹200 gives
              a ₹4,400 monthly limit; this is an example, not a fixed monthly entitlement. Excess or ineligible voucher value stays taxable.</p>
            <p>The full coupon value goes to your meal card, so it is excluded from cash take-home even when tax-exempt.
              This calculator allocates coupons and employer NPS from Special Allowance within CTC, with employer NPS
              allocated first. If that allowance is insufficient, an on-screen message shows the amounts actually applied.</p>
            <p>For FY 2025–26 the earlier limit was ₹50 per eligible meal and paid-voucher relief was unavailable in the
              new regime. The current calculator uses Tax Year 2026–27 rules. Confirm your employer's payroll treatment.</p>
            <a className="text-indigo-600 underline" href="https://www.incometax.gov.in/iec/foportal/sites/default/files/2026-03/En-Notified-IT-Rules-2026-20-03-2026.pdf#page=14" target="_blank" rel="noopener noreferrer">Official notified rules: meal benefits (page 14)</a>
          </div>
        </details>
      </div>

      <div className="bg-yellow-50 rounded-lg p-6">
        <h3 className="text-yellow-800 font-medium mb-2">Important Note</h3>
        <p className="text-sm text-yellow-700">
          This calculator provides estimates based on standard deductions and tax rates.
          Actual figures may vary based on your specific situation, tax regime choice, and
          company policies.
        </p>
      </div>
    </div>
  );
}

export default InfoPanel;
